import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2 } from 'lucide-react';
import { fetchSummary, selectSummary } from '../store/summarySlice.js';

const SummaryTab = () => {
  const dispatch = useDispatch();
  const { loading, error, rows, headers, grandTotals } = useSelector(selectSummary);

  useEffect(() => {
    dispatch(fetchSummary());
  }, [dispatch]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 text-gray-500 animate-spin" />
        <span className="ml-3 text-gray-600">Loading summary...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-6 p-4 rounded-xl border-l-4 bg-red-50 border-red-500 text-red-800">
        {error}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No summary data available.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-300 shadow-md">
      <table className="min-w-full divide-y divide-gray-300">
        {/* Table Header */}
        <thead className="bg-cyan-900 text-white">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
              Provider
            </th>
            {headers.map(header => (
              <th key={header} scope="col" className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
                {header}
              </th>
            ))}
            <th scope="col" className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
              Grand Total
            </th>
          </tr>
        </thead>

        {/* Table Body */}
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.map((row, idx) => (
            <tr key={row.provider} className={idx % 2 === 0 ? 'bg-cyan-50' : 'bg-white'}>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                {row.provider}
              </td>
              {headers.map(header => {
                const status = row.statuses.find(s => s.status === header);
                const count = status ? status.count : '';
                return (
                  <td key={`${row.provider}-${header}`} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {count}
                  </td>
                );
              })}
              <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-800">
                {row.totalCount}
              </td>
            </tr>
          ))}
        </tbody>

        {/* Table Footer (Grand Totals) */}
        <tfoot className="bg-cyan-900 text-white">
          <tr>
            <th scope="row" className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
              Grand Total
            </th>
            {headers.map(header => (
              <td key={`total-${header}`} className="px-6 py-3 text-left text-sm font-bold">
                {grandTotals[header] || 0}
              </td>
            ))}
            <td className="px-6 py-3 text-left text-sm font-bold">
              {grandTotals['Grand Total'] || 0}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default SummaryTab;
